import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { costEngineOccupancyTable, type OccupancyRow } from "@/services/pricing";

/**
 * PRICEBOOK-1 · 成本概览价格单元格的悬停小标签：1/2/3/4 人一房的 同行建议售价 / 同行结算价 / 散客价 / API 价。
 * 数据走已有 occupancy-table 端点（b2b / direct / api 三个渠道），第一次悬停时才取，react-query 缓存；不住宿的产品不分房，不弹。
 */
const usd2 = (c?: number | null) => (c == null ? "—" : `$${(c / 100).toFixed(2)}`);
const LABEL: Record<number, string> = { 1: "单人房", 2: "2 人一间", 3: "3 人一间", 4: "4 人一间" };

export function PriceHover({ productId, children }: { productId: string; children: ReactNode }) {
  const [hovered, setHovered] = useState(false);
  const opts = { staleTime: 60_000, enabled: hovered } as const;
  const b2b = useQuery({ queryKey: ["pricing", "hover-occ", productId, "b2b"], queryFn: () => costEngineOccupancyTable(productId, { channel: "b2b" }), ...opts });
  const direct = useQuery({ queryKey: ["pricing", "hover-occ", productId, "direct"], queryFn: () => costEngineOccupancyTable(productId, { channel: "direct" }), ...opts });
  const api = useQuery({ queryKey: ["pricing", "hover-occ", productId, "api"], queryFn: () => costEngineOccupancyTable(productId, { channel: "api" }), ...opts });

  const rows: OccupancyRow[] = b2b.data ?? [];
  const overnight = rows.length > 0 ? rows[0].overnight !== false : true;
  const by = (q: OccupancyRow[] | undefined, o: number) => q?.find((r) => r.occupancy === o);
  const loading = b2b.isLoading || direct.isLoading || api.isLoading;
  const failed = b2b.isError || direct.isError || api.isError;

  return (
    <span className="group relative inline-block" onMouseEnter={() => setHovered(true)} data-testid="price-hover">
      {children}
      {hovered && overnight && (
        <span role="tooltip" className="pointer-events-none absolute left-0 top-full z-30 mt-1 hidden w-[340px] rounded-lg border border-slate-200 bg-white p-2.5 text-left shadow-lg animate-in fade-in zoom-in-95 group-hover:block" data-testid="price-hover-tip">
          {loading ? (
            <span className="text-[12px] text-slate-400">加载中…</span>
          ) : failed ? (
            <span className="text-[12px] text-rose-600">加载失败</span>
          ) : (
            <table className="w-full text-[12px] tabular-nums">
              <thead className="text-[11px] text-slate-500">
                <tr><th className="pb-1 text-left font-medium">住房</th><th className="pb-1 text-right font-medium">同行建议售价</th><th className="pb-1 text-right font-medium">同行结算价</th><th className="pb-1 text-right font-medium">散客价</th><th className="pb-1 text-right font-medium">API 价</th></tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4].map((o) => (
                  <tr key={o} className="border-t border-slate-100" data-testid={`hover-row-${o}`}>
                    <td className="py-1 text-slate-600">{LABEL[o]}</td>
                    <td className="py-1 text-right text-slate-900">{usd2(by(b2b.data, o)?.list_price_cents)}</td>
                    <td className="py-1 text-right text-blue-700">{usd2(by(b2b.data, o)?.company_net_cents)}</td>
                    <td className="py-1 text-right text-slate-600">{usd2(by(direct.data, o)?.list_price_cents)}</td>
                    <td className="py-1 text-right text-slate-900">{usd2(by(api.data, o)?.list_price_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </span>
      )}
    </span>
  );
}
